import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Film, 
  ArrowRight, 
  Check,
  Trophy,
  Sparkles,
  FileText,
  Download,
  Gift,
  Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';

const WelcomeBack = () => {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Minimal Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Film className="w-6 h-6 text-primary" />
            <span className="font-black text-lg tracking-tight">MOVIEBOT</span>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-primary font-medium text-sm tracking-wider mb-6">
              TO OUR EARLIEST SUPPORTERS
            </p>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6">
              You signed up.<br />
              We built it.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-400">
                It's finally here.
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-lg mx-auto leading-relaxed mb-10">
              MovieBot is live. The Summer Film Competition is open. And you're first in line.
            </p>

            <a href="https://moviebot.tech/signup">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-lg px-8 py-6">
                Start Writing — Act 1 is Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* A Note From the Founder */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white/[0.02] border border-white/5 p-8 md:p-12"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-amber-500/20 rounded-full flex items-center justify-center text-lg font-bold">
                AF
              </div>
              <div>
                <p className="text-white font-medium">Amir Foster</p>
                <p className="text-sm text-muted-foreground">Founder, MovieBot</p>
              </div>
            </div>

            <div className="space-y-6 text-muted-foreground leading-relaxed">
              <p>
                <span className="text-white">Thank you for signing up when there was nothing to sign up for.</span>
              </p>
              
              <p>
                Months ago, you gave us your email based on a promise. You believed in what we were building before we'd built it. That means something. Actually, it means everything.
              </p>
              
              <p>
                Since then, we've shipped the full product: an AI-guided screenwriting platform that walks you through the proven 3-act structure used by every Hollywood film. You stay the author. The AI handles the craft. Act 1 is free, forever.
              </p>
              
              <p>
                Here's what just happened: <span className="text-white">179 paying customers. Visitors from 186 countries. Our first subscriber came in on the top Pro tier.</span> And the Summer Film Competition is officially live.
              </p>
              
              <p className="text-white">
                You were here first, so I want you to have the first real shot at the grand prize — full production and streaming submission.
              </p>
              
              <p>
                Welcome back. Let's write something.
              </p>
              
              <p className="text-white font-medium pt-4">
                — Amir
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's New */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl font-bold text-white mb-8">What's shipped since you signed up</h2>
            
            <div className="space-y-4">
              {[
                {
                  icon: Sparkles,
                  title: 'The full guided writing experience',
                  description: 'Act 1 → Act 2 → Act 3, beat by beat. AI generates options, you pick the direction.'
                },
                {
                  icon: FileText,
                  title: 'Hollywood-standard formatting',
                  description: 'Proper screenplay format from the first scene. No manual formatting ever.'
                },
                {
                  icon: Download,
                  title: 'PDF & Fountain export',
                  description: 'Download your script in industry-standard formats.'
                },
                {
                  icon: Trophy,
                  title: 'Summer Film Competition',
                  description: 'Submit your screenplay. We produce the winner and submit to streaming.'
                },
                {
                  icon: Heart,
                  title: 'Act 1 free, forever',
                  description: 'Start your story with zero risk. Upgrade when you\'re ready to finish it.'
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1, duration: 0.4 }}
                  className="flex gap-4 p-4 bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors"
                >
                  <div className="w-10 h-10 bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Legacy User Perk */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-primary/10 via-amber-500/5 to-transparent border border-primary/20 p-8 md:p-10 text-center"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/20 rounded-full mb-6">
              <Gift className="w-7 h-7 text-primary" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-3">
              A thank you for believing early
            </h2>
            
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              You signed up before we had a product. That kind of trust deserves something back.
            </p>
            
            <div className="bg-black/30 border border-white/10 p-6 mb-2 inline-block">
              <p className="text-3xl font-black text-white mb-1">Pro Features, Basic Price</p>
              <p className="text-primary font-medium">Pay $3.99/mo, get the full $9.99/mo experience</p>
            </div>
            
            <p className="text-xs text-muted-foreground mb-6">You keep your Basic subscription — we just unlock Pro for you.</p>
            
            <div className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              <p className="mb-2">Here's how it works:</p>
              <ol className="text-left space-y-2">
                <li className="flex gap-2">
                  <span className="text-primary font-bold">1.</span>
                  <span>Try MovieBot free today — Act 1 is on us</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary font-bold">2.</span>
                  <span>When you're ready, subscribe to Basic ($3.99/mo)</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary font-bold">3.</span>
                  <span>Email <span className="text-white">Admin@moviebot.net</span> — we'll upgrade you to Pro at no extra cost</span>
                </li>
              </ol>
            </div>
            
            <a href="https://moviebot.tech/signup">
              <Button className="bg-white text-black hover:bg-white/90 font-bold px-8 py-5">
                Try Free Today
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* The Challenge */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Trophy className="w-6 h-6 text-teal-400" />
              <h2 className="text-2xl font-bold text-white">Summer Film Competition</h2>
            </div>
            
            <div className="bg-gradient-to-br from-teal-500/10 to-emerald-500/5 border border-teal-500/20 p-6 md:p-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" />
                <span className="text-teal-400 font-mono text-sm">OPEN NOW</span>
              </div>
              
              <p className="text-lg text-white mb-4">
                <span className="font-bold">Full short film production</span> for the winning screenplay.
              </p>
              
              <ul className="space-y-2 text-muted-foreground mb-6">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-teal-400" />
                  <span>Submit before Summer 2026 deadline</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-teal-400" />
                  <span>Free to enter with any active subscription</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-teal-400" />
                  <span>Winner gets produced and submitted to streaming</span>
                </li>
              </ul>
              
              <Link to="/challenge">
                <Button variant="outline" className="border-teal-500/30 text-teal-400 hover:bg-teal-500/10">
                  Full Competition Details
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Your story's been waiting long enough.
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start writing.
            </p>
            
            <a href="https://moviebot.tech/signup">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-lg px-10 py-6">
                Start Act 1 — Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            
            <p className="mt-6 text-sm text-muted-foreground">
              No credit card required. Just you and your story.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-2xl mx-auto flex flex-col items-center gap-4 text-center">
          <div className="flex items-center gap-2">
            <Film className="w-5 h-5 text-primary" />
            <span className="font-bold">MOVIEBOT</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Thank you for being one of the first 3,100.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default WelcomeBack;
